import {
    Component,
    OnInit,
    ViewChild,
    effect,
    inject,
    input,
    output,
    ChangeDetectionStrategy
} from '@angular/core';
import {
    AbstractControl,
    FormBuilder,
    ValidationErrors,
    ValidatorFn
} from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { UserCto } from 'src/app/api/models';
import { UserService } from 'src/app/api/services';
import { UserSearchStateService } from './user-search-state.service';

export const userSearchValidator: ValidatorFn = (
    control: AbstractControl
): ValidationErrors | null => {
    const firstName = control.get('firstName');
    const lastName = control.get('lastName');
    const email = control.get('email');
    const userGuid = control.get('userGuid');
    const userId = control.get('userId');
    const apiUserId = control.get('apiUserId');
    const apiUserCompanyId = control.get('apiUserCompanyId');
    const companyId = control.get('companyId');

    if (
        email?.value ||
        userGuid?.value ||
        userId?.value ||
        apiUserId?.value ||
        apiUserCompanyId?.value ||
        (firstName?.value && lastName?.value) ||
        companyId?.value
    ) {
        return null;
    }

    if (firstName?.value && !lastName?.value) {
        return { lastNameRequired: true };
    }

    if (!firstName?.value && lastName?.value) {
        return { firstNameRequired: true };
    }

    return { oneOfRequired: true };
};

@Component({
    selector: 'app-user-search',
    templateUrl: './user-search.component.html',
    styleUrls: ['./user-search.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UserSearchComponent implements OnInit {
    private readonly fb = inject(FormBuilder);
    private readonly userService = inject(UserService);
    readonly searchState = inject(UserSearchStateService);

    readonly userSelected = output<string>();
    readonly userObjectSelected = output<UserCto>();

    readonly selectionType = input<'guid' | 'long'>('guid');

    readonly userSearchTitle = input<string>('User Search');

    public displayedColumns: string[] = [
        'select',
        'firstName',
        'lastName',
        'email',
        'company',
        'userGuid',
        'userId'
    ];

    public userDataSource: MatTableDataSource<UserCto> =
        new MatTableDataSource<UserCto>();
    public userCount = 0;
    private usersPaginator: MatPaginator | undefined;

    @ViewChild(MatPaginator) set matPaginator(mp: MatPaginator) {
        this.usersPaginator = mp;
        this.userDataSource.paginator = this.usersPaginator;
    }

    public userSearchForm = this.fb.group(
        {
            firstName: [null as string | null],
            lastName: [null as string | null],
            userGuid: [null as string | null],
            userId: [null as number | null],
            apiUserId: [null as string | null],
            apiUserCompanyId: [null as string | null],
            email: [null as string | null],
            companyId: [null as number | null]
        },
        { validators: userSearchValidator }
    );

    // Effect runs automatically when form values change
    private readonly _formStateEffect = effect(() => {
        const formValue = this.userSearchForm.value;
        this.searchState.setSearchCriteria(formValue);
    });

    // Effect runs automatically when search results change
    private readonly _searchResultsEffect = effect(() => {
        const searchResults = this.searchState.searchResults();
        if (searchResults) {
            this.updateDataSource(searchResults);
        }
    });

    ngOnInit(): void {
        // Restore form from saved state
        const savedCriteria = this.searchState.searchCriteria();
        if (savedCriteria && Object.keys(savedCriteria).length > 0) {
            this.userSearchForm.patchValue(savedCriteria, { emitEvent: false });
        }
    }

    get email(): string | undefined {
        return this.userSearchForm.controls.email.value ?? undefined;
    }

    get firstName(): string | undefined {
        return this.userSearchForm.controls.firstName.value ?? undefined;
    }

    get lastName(): string | undefined {
        return this.userSearchForm.controls.lastName.value ?? undefined;
    }

    get userGuid(): string | undefined {
        return this.userSearchForm.controls.userGuid.value ?? undefined;
    }

    get userId(): number | undefined {
        return this.userSearchForm.controls.userId.value ?? undefined;
    }

    get apiUserId(): string | undefined {
        return this.userSearchForm.controls.apiUserId.value ?? undefined;
    }

    get apiUserCompanyId(): string | undefined {
        return this.userSearchForm.controls.apiUserCompanyId.value ?? undefined;
    }

    get companyId(): number | undefined {
        return this.userSearchForm.controls.companyId.value ?? undefined;
    }

    public search(): void {
        if (this.userSearchForm.invalid) {
            return;
        }
        this.searchState.setLoading(true);
        this.userService
            .getUsers$Json({
                Email: this.email,
                ApiUserId: this.apiUserId,
                FirstName: this.firstName,
                LastName: this.lastName,
                UserGuid: this.userGuid,
                UserId: this.userId,
                ApiUserCompanyId: this.apiUserCompanyId,
                CompanyId: this.companyId
            })
            .subscribe((users) => {
                this.searchState.setLoading(false);
                this.searchState.setSearchResults(users);
                this.updateDataSource(users);
            });
    }

    private updateDataSource(users: UserCto[]): void {
        this.userDataSource = new MatTableDataSource(users);
        this.userCount = users.length;
        if (this.usersPaginator) {
            this.userDataSource.paginator = this.usersPaginator;
        }
    }

    public reset(): void {
        this.userSearchForm.reset();
        this.searchState.clearState();
        this.userDataSource = new MatTableDataSource<UserCto>();
        this.userCount = 0;
    }

    public selectUser(user: UserCto): void {
        this.userSelected.emit(
            this.selectionType() == 'guid'
                ? (user.userGuid ?? '')
                : (user.userId?.toString() ?? '')
        );

        this.userObjectSelected.emit(user);
    }
}
